import dayjs from 'dayjs'
import { fileUrl } from 'src/utils/lib'
import 'dayjs/locale/th'
import buddhistEra from 'dayjs/plugin/buddhistEra'
import _ from 'lodash'
import { addEnroll, setReduxValue } from 'src/redux/slices/class/enroll'
import { startLoading, stopLoading } from 'src/redux/slices/loading'
import { USER_TYPE } from 'src/modules/EExamination/constant'
import { fetchMyEnrollProfile } from 'src/services/class/registration'

export const convertDateShortTh = (date) => {
  dayjs.extend(buddhistEra)
  dayjs.locale('th')
  return dayjs(date).format('DD MMM BBBB')
}

export const handleSelfRegister = () => async (dispatch) => {
  const user = JSON.parse(window.localStorage.getItem('user'))
  const localUserType = _.get(user, 'userType', 'STAFF')
  const localRole = _.get(user, 'agentRole', '')
  const localAbbreviation = _.get(user, 'distributionChannel.abbreviation', '')
  const selfRegister = !(
    localUserType === 'AGENT' &&
    localRole === 'Manager' &&
    (localAbbreviation === 'AGC' || localAbbreviation === 'CS')
  )
  dispatch(
    setReduxValue({
      index: 'selfRegister',
      value: selfRegister,
    })
  )
}

export const fetchInitials = () => async (dispatch) => {
  dispatch(startLoading())
  dispatch(handleSelfRegister())

  const result = await dispatch(checkProspectDetail()) // ✅ รอให้เสร็จ
  const success = result?.meta?.requestStatus === 'fulfilled'
  if (success) {
    dispatch(setReduxValue({ index: 'isInitialLoaded', value: true }))
  } else {
    console.warn('❌ ไม่สามารถโหลดข้อมูล enroll ได้')
  }

  dispatch(stopLoading())
}

export const checkProspectDetail = () => async (dispatch) => {
  const result = await dispatch(fetchMyEnrollProfile())
  const prospectDetail = result?.payload
  if (prospectDetail) {
    // 🟢 ดำเนินการต่อจากข้อมูลใน localStorage หรือที่ได้จาก API
    const userImageUrl = prospectDetail.image
      ? fileUrl(prospectDetail.image)
      : '/logo/staff_profile_small.png'

    const type = prospectDetail?.type || 'TEMP'
    const isCanEditProfile = prospectDetail?.isCanEditProfile || false

    // Get user type from localStorage to determine role
    // Use userType from localStorage only, don't fallback to type from payload
    const user = JSON.parse(window.localStorage.getItem('user') || '{}')
    const userType = user?.userType
    const staffType = _.get(user, 'staffProfile.type', null)
    const isAgent = userType === 'AGENT'
    const isStaffAcademy = userType === 'STAFF' && staffType !== 'NON_ACADEMY'
    const isStaffNonAcademy =
      userType === 'STAFF' && staffType === 'NON_ACADEMY'

    // Helper function to check if field has value
    const hasValue = (value) => value && value.trim() !== ''

    // Determine editable state based on role requirements
    let editable = {}

    if (isAgent) {
      // Agent: Only prefixTH editable if no value (first time), other fields not editable
      editable = {
        isEditPrefixTH: !hasValue(prospectDetail?.prefixTH),
        isEditName: false,
        isEditSurname: false,
        isEditPrefix: !hasValue(prospectDetail?.prefix),
        isEditNameEN: false,
        isEditSurnameEN: false,
        isEditIdCardNo: false,
        isEditMobile: false,
        isEditBirthDate: false,
      }
    } else if (isStaffAcademy) {
      // Staff Academy: ID Card and DOB editable only if no value, other fields editable only if no value
      editable = {
        isEditPrefixTH: !hasValue(prospectDetail?.prefixTH),
        isEditName: !hasValue(prospectDetail?.firstNameTH),
        isEditSurname: !hasValue(prospectDetail?.lastNameTH),
        isEditPrefix: !hasValue(prospectDetail?.prefix),
        isEditNameEN: !hasValue(prospectDetail?.firstName),
        isEditSurnameEN: !hasValue(prospectDetail?.lastName),
        isEditIdCardNo: !hasValue(prospectDetail?.idCardNo),
        isEditMobile: !hasValue(prospectDetail?.mobileNo),
        isEditBirthDate: !hasValue(prospectDetail?.dateOfBirth),
      }
    } else if (isStaffNonAcademy) {
      // Staff Non Academy: All fields editable only if no value
      editable = {
        isEditPrefixTH: !hasValue(prospectDetail?.prefixTH),
        isEditName: !hasValue(prospectDetail?.firstNameTH),
        isEditSurname: !hasValue(prospectDetail?.lastNameTH),
        isEditPrefix: !hasValue(prospectDetail?.prefix),
        isEditNameEN: !hasValue(prospectDetail?.firstName),
        isEditSurnameEN: !hasValue(prospectDetail?.lastName),
        isEditIdCardNo: !hasValue(prospectDetail?.idCardNo),
        isEditMobile: !hasValue(prospectDetail?.mobileNo),
        isEditBirthDate: !hasValue(prospectDetail?.dateOfBirth),
      }
    } else {
      // Default: For Prospect and other types - editable if no data or if can edit profile
      // If no data, allow editing; if has data, check isCanEditProfile
      editable = {
        isEditPrefixTH:
          (type === USER_TYPE.AGENT && !hasValue(prospectDetail?.prefixTH)) ||
          (type !== USER_TYPE.AGENT &&
            (!hasValue(prospectDetail?.prefixTH) || isCanEditProfile)),
        isEditName:
          type !== USER_TYPE.AGENT &&
          (!hasValue(prospectDetail?.firstNameTH) || isCanEditProfile),
        isEditSurname:
          type !== USER_TYPE.AGENT &&
          (!hasValue(prospectDetail?.lastNameTH) || isCanEditProfile),
        isEditPrefix:
          type !== USER_TYPE.AGENT &&
          (!hasValue(prospectDetail?.prefix) || isCanEditProfile),
        isEditNameEN:
          type !== USER_TYPE.AGENT &&
          (!hasValue(prospectDetail?.firstName) || isCanEditProfile),
        isEditSurnameEN:
          type !== USER_TYPE.AGENT &&
          (!hasValue(prospectDetail?.lastName) || isCanEditProfile),
        isEditIdCardNo:
          !(type === USER_TYPE.AGENT || type === USER_TYPE.TEMP) &&
          (!hasValue(prospectDetail?.idCardNo) || isCanEditProfile),
        isEditMobile:
          type !== USER_TYPE.AGENT &&
          (!hasValue(prospectDetail?.mobileNo) || isCanEditProfile),
        isEditBirthDate:
          !(type === USER_TYPE.AGENT || type === USER_TYPE.TEMP) &&
          (!hasValue(prospectDetail?.dateOfBirth) || isCanEditProfile),
      }
    }

    const data = [
      {
        uuid: prospectDetail?.uuid || '',
        type: type,
        image: userImageUrl,
        prefixTH: prospectDetail?.prefixTH || '',
        firstNameTH: prospectDetail?.firstNameTH || '',
        lastNameTH: prospectDetail?.lastNameTH || '',
        prefix: prospectDetail?.prefix || '',
        firstName: prospectDetail?.firstName || '',
        lastName: prospectDetail?.lastName || '',
        mobileNo: prospectDetail?.mobileNo?.replace(/-/g, '') || '',
        idCardNo: prospectDetail?.idCardNo || '',
        email: prospectDetail?.email || '',
        isEdit: false,
        birthDate: prospectDetail?.dateOfBirth || '',
        isProfileUpdated: prospectDetail?.isProfileUpdated,
        editable: editable,
        isCanEditProfile: isCanEditProfile,
      },
    ]

    dispatch(setReduxValue({ index: 'enroll', value: data }))
    dispatch(setReduxValue({ index: 'formError', value: [[]] }))
    dispatch(setReduxValue({ index: 'enrollFormError', value: [true] }))
    dispatch(setReduxValue({ index: 'formNextButtonDisabled', value: [true] }))
    dispatch(addEnroll())

    return result
  } else {
    console.warn('❌ ไม่สามารถโหลดข้อมูลผู้สมัครได้')
    return
  }
}
